import type { Pageable } from "bancojs";

/**
 * Conversión entre el modelo de orden de Vuetify y el de Spring Data.
 *
 * Vive aparte del componente para poder testearla sin montar nada: es la pieza
 * más frágil de la migración de la tabla.
 *
 * Vuetify 2 usaba DOS arrays paralelos:
 *     sortBy:   ["name"]      sortDesc: [true]
 * Vuetify 3+ usa uno de objetos:
 *     sortBy:   [{ key: "name", order: "desc" }]
 * y Spring Data espera una cadena por campo:
 *     sort=name,desc
 *
 * Un error acá es SILENCIOSO: la tabla ordena por la columna equivocada, o la
 * API devuelve 500 por un nombre de campo inexistente. Nada falla al compilar.
 */

/** La forma que usa Vuetify 3+ (`SortItem`). */
export interface VuetifySortItem {
  key: string;
  order?: boolean | "asc" | "desc";
}

/**
 * `sort` de Spring Data → `sortBy` de Vuetify.
 *
 * Acepta la cadena única que usan las vistas (`"id,asc"`) y también un array,
 * porque Spring admite varios criterios.
 */
export function toVuetifySort(sort: Pageable["sort"]): VuetifySortItem[] {
  if (!sort) return [];
  const entries = Array.isArray(sort) ? sort : [sort];
  return entries
    .map((entry) => {
      const [key, direction] = String(entry).split(",");
      if (!key) return null;
      // Spring asume ASC cuando no se indica dirección.
      return { key, order: direction === "desc" ? "desc" : "asc" } as VuetifySortItem;
    })
    .filter((s): s is VuetifySortItem => s !== null);
}

/**
 * `sortBy` de Vuetify → `sort` de Spring Data.
 *
 * `order` puede venir como booleano en Vuetify (true = desc), de ahí la
 * normalización. Sin criterio de orden devuelve `fallbackKey`, porque un
 * `Page` sin ordenar no garantiza estabilidad entre páginas: sin ORDER BY,
 * Postgres puede repetir o saltarse filas al paginar.
 */
export function toSpringSort(
  sortBy: readonly VuetifySortItem[] | undefined,
  fallbackKey: string
): string {
  const first = sortBy?.[0];
  if (!first?.key) return `${fallbackKey},asc`;
  const order = first.order === "desc" || first.order === true ? "desc" : "asc";
  return `${first.key},${order}`;
}

/** Los eventos `@update:options` que emite v-data-table-server. */
export interface VuetifyTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: readonly VuetifySortItem[];
}

/**
 * Evento de Vuetify → `Pageable` del SDK.
 *
 * Vuetify pagina desde 1 y Spring desde 0: ese desfase de uno es el error
 * clásico de esta conversión, y hace que la primera página se salte registros.
 */
export function toPageable(
  options: VuetifyTableOptions,
  fallbackKey: string
): Pageable {
  return {
    page: Math.max(0, (options.page ?? 1) - 1),
    size: options.itemsPerPage,
    sort: toSpringSort(options.sortBy, fallbackKey),
  };
}
